import { useNavigate } from "@solidjs/router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { isAxiosError } from "axios";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import {
  axiosInstance,
  removeAuthToken,
  setAuthToken,
  setRefreshToken,
} from "@/api/client";
import { useI18n } from "@/api/i18n";
import { AUTH_KEY } from "@/api/query-keys";
import { LIVE_ENV } from "@/constants/env";
import { clearOnboardingStore } from "@/domains/auth/onboarding/context/OnboardingContext";
import { clearCompanyStore } from "@/domains/company/company/store/company-store";
import { clearAllCompanyOnboardingDrafts } from "@/domains/company/onboarding/store/draft-store";
import { clearLegacyJobOfferDrafts } from "@/domains/job-offer/create/utils/legacy-drafts";
import { clearVisitedJobStore } from "@/domains/job-offer/store/visited_job";
import { usePostHog } from "@/hooks/usePostHog";
import { useAuthV2 } from "../context/auth-provider";
import type {
  ImpersonateCompanyRequestBody,
  ImpersonateSuccessResponse,
  ImpersonateUserRequestBody,
  Login,
  LoginResponse,
  RefreshTokenResponse,
  Signup,
  VerifyTokenResponse,
} from "./type";

export const USER_COMPANIES_QUERY_KEY = "user-companies";

//V2 API
const signup = async ({
  email,
  name,
  nationality,
  next,
  newsletterOptIn,
  signupIntent,
}: Signup) => {
  const { data } = await axiosInstance.post<LoginResponse>("/register", {
    email,
    name,
    nationality,
    // Stesso nome del login: il backend accetta ancora 'callbackUrl', ma è un
    // alias storico e il magic link porta comunque 'next'.
    next,
    ...(newsletterOptIn !== undefined && { newsletterOptIn }),
    // Omesso quando la scelta non è completa: il backend lascia allora la
    // colonna a `null`, che significa "non lo sappiamo" e non "cerca progetti".
    ...(signupIntent !== undefined && { signupIntent }),
    env: LIVE_ENV,
  });
  return data;
};

export const useSignup = () => {
  return useMutation(() => ({
    mutationFn: signup,
    onSuccess: async () => {
      await Promise.all([
        clearLegacyJobOfferDrafts(),
        clearOnboardingStore(),
        clearVisitedJobStore(),
      ]);
    },
  }));
};
const sendVerificationEmail = async ({ email }: { email: string }) => {
  const { data } = await axiosInstance.post<LoginResponse>(
    "/send-verification-email",
    {
      email,
    },
  );
  return data;
};

export const useSendVerificationEmail = () => {
  return useMutation(() => ({
    mutationFn: sendVerificationEmail,
  }));
};

const login = async ({ email, provider, next }: Login) => {
  const { data } = await axiosInstance.post<LoginResponse>("/login", {
    email,
    provider,
    next,
    env: LIVE_ENV,
  });
  return data;
};

export const useLoginWithMagicLink = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: ({ email, next }: Pick<Login, "email" | "next">) =>
      login({ email, provider: "magic_link", next }),
    onSuccess: () => {
      queryClient.clear();
    },
  }));
};

const verifyToken = async (token: string, next?: string) => {
  // `next` is forwarded so the backend can sanitize it and hand back a value we
  // can trust, instead of us being the only gate on a query param.
  const { data } = await axiosInstance.get<VerifyTokenResponse>(
    `/verify/${token}`,
    next ? { params: { next } } : undefined,
  );
  setAuthToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  return data;
};

export const useVerifyToken = (token: string, next?: string) => {
  return useQuery(() => ({
    queryKey: [AUTH_KEY],
    queryFn: () => verifyToken(token, next),
    enabled: !!token,
  }));
};

export { getVerifyErrorKind } from "./verify-error";

export const handleRefreshToken = async (refreshToken?: string) => {
  const { data } = await axiosInstance.post<RefreshTokenResponse>(
    "/refresh-token",
    {
      refreshToken,
    },
  );

  return data;
};

// Impersonation API functions
const impersonateUser = async (body: ImpersonateUserRequestBody) => {
  const { data } = await axiosInstance.post<ImpersonateSuccessResponse>(
    "/impersonate/user",
    body,
  );
  return data;
};

const impersonateCompany = async (body: ImpersonateCompanyRequestBody) => {
  const { data } = await axiosInstance.post<ImpersonateSuccessResponse>(
    "/impersonate/company",
    body,
  );
  return data;
};

export const useImpersonateUser = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: impersonateUser,
    onSuccess: async (data) => {
      // Set the new tokens
      await setAuthToken(data.accessToken);
      await setRefreshToken(data.refreshToken);
      // Clear all queries to refresh the user state
      queryClient.clear();
    },
  }));
};

export const useImpersonateCompany = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: impersonateCompany,
    onSuccess: async (data) => {
      // Set the new tokens
      await setAuthToken(data.accessToken);
      await setRefreshToken(data.refreshToken);
      // Clear all queries to refresh the user state
      queryClient.clear();
    },
  }));
};

type ChangeUserPasswordPayload = {
  currentPassword: string;
  newPassword: string;
};

const changeUserPassword = async (payload: ChangeUserPasswordPayload) => {
  const { data } = await axiosInstance.post("/change-password", payload);
  return data;
};

export const useChangeUserPassword = () => {
  return useMutation(() => ({
    mutationFn: changeUserPassword,
  }));
};

const deleteUser = async () => {
  const { data } = await axiosInstance.delete("/delete-user");
  return data;
};

export const useDeleteUser = () => {
  const [, setAuthStoreV2] = useAuthV2();
  const { reset } = usePostHog();
  const { t } = useI18n();
  const navigate = useNavigate();

  // Logout completo + redirect: usato sia in caso di successo (200) sia
  // quando il backend risponde 404 (utente già inesistente lato server).
  const clearSessionAndRedirect = async () => {
    setAuthStoreV2(
      produce((state) => {
        state.profile = null;
        state.companies = null;
        state.user = null;
        state.user_applications = null;
      }),
    );
    clearLegacyJobOfferDrafts();
    clearOnboardingStore();
    clearVisitedJobStore();
    reset();

    // This is the only teardown site that leaves via `window.location.href`
    // instead of the client-side router: a real document unload. On IndexedDB
    // (the production localforage driver) these wipes are live transactions
    // that the unload can cut in half, leaving the half-finished company
    // onboarding draft — company name, VAT number, address, phone — at rest on
    // the device of a user who just deleted their account (PLAN D15: on the
    // shared-device trade privacy beats the resume promise).
    //
    // `allSettled`, not `all`: a failing storage driver must never strand the
    // user on a page whose session is already gone.
    await Promise.allSettled([
      removeAuthToken(),
      clearCompanyStore(),
      clearAllCompanyOnboardingDrafts(),
    ]);

    window.location.href = "/auth";
  };

  return useMutation(() => ({
    mutationFn: deleteUser,
    onSuccess: clearSessionAndRedirect,
    onError: async (error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;

      // 404 → l'utente non esiste già più lato server: trattalo come cancellazione riuscita.
      if (status === 404) {
        await clearSessionAndRedirect();
        return;
      }

      // 409 → l'utente è il contatto principale di un'azienda con altri membri.
      // NON fare logout: mostra un messaggio dedicato e linka alla gestione membri.
      if (status === 409) {
        toast.error(
          t("profile.tabs.settings.dialog.deleteAccount.ownerConflict"),
          {
            duration: 10000,
            action: {
              label: t(
                "profile.tabs.settings.dialog.deleteAccount.ownerConflictAction",
              ),
              onClick: () => navigate("/profile/company/members"),
            },
          },
        );
        return;
      }

      // 401 → gestito globalmente dall'interceptor axios (refresh token / redirect al login).
      if (status === 401) {
        return;
      }

      // 500 e ogni altro errore → messaggio generico; l'utente resta loggato e può riprovare.
      toast.error(t("profile.tabs.settings.dialog.deleteAccount.genericError"));
    },
  }));
};

const checkEmailExists = async (email: string) => {
  const { data } = await axiosInstance.post<{ exists: boolean }>(
    "/check-email",
    { email },
  );
  return data;
};

export const useCheckEmailExists = () => {
  return useMutation(() => ({
    mutationFn: checkEmailExists,
  }));
};
